import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { Button } from '@rentspree/component-2023.components.atoms.button';
import Divider from '@rentspree/component-2023.components.atoms.divider';
import { IconButton } from '@rentspree/component-2023.components.atoms.icon-button';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { LinearProgressStep } from '@rentspree/component-2023.components.organisms.linear-progress-step';
import { StickyFooterTemplate } from '@rentspree/component-2023.components.templates.sticky-footer-template';

export interface MultiStepLayoutProps {
  currentStep: number;
  total: number;
  currentSubStepPercentage: number;
  onHomeClick?: (() => void) | undefined;
  onExitClick?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  backDisabled?: boolean | undefined;
  nextDisabled?: boolean | undefined;
  backLabel?: string | undefined;
  nextLabel?: string | undefined;
  children: React.ReactNode;
  showDividerInsteadOfProgress?: boolean;
  nextButtonVariant?: 'text' | 'outlined' | 'contained' | undefined;
  nextButtonColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | undefined;
  title?: string | undefined;
  description?: string | undefined;
  // Allow wider layout for steps that need more horizontal space (e.g., property type cards)
  wideLayout?: boolean | undefined;
}

export const MultiStepLayout: React.FC<MultiStepLayoutProps> = ({
  currentStep,
  total,
  currentSubStepPercentage,
  onHomeClick,
  onExitClick,
  onBack,
  onNext,
  backDisabled,
  nextDisabled,
  backLabel = 'Back',
  nextLabel = 'Next',
  children,
  showDividerInsteadOfProgress = false,
  nextButtonVariant = 'contained',
  nextButtonColor = 'secondary',
  title,
  description,
  wideLayout = false,
}) => {
  const navigate = useNavigate();

  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      navigate('/');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        height: '100vh', // Fixed height to prevent footer from disappearing
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 3.7rem',
          minHeight: '64px',
        }}
      >
        <div style={{ backgroundColor: '#110000', borderRadius: '8px' }}>
          <IconButton
            onClick={handleHomeClick}
            aria-label='Home'
            color='white'
            sx={{
              width: '32px',
              height: '32px',
              '& .MuiSvgIcon-root': {
                fontSize: '20px',
              },
            }}
          >
            <HomeIcon />
          </IconButton>
        </div>
        <Button
          onClick={onExitClick || (() => {})}
          variant='outlined'
          size='small'
          color='secondary'
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            minWidth: '58px',
            height: '38px',
            padding: '8px 16px',
            borderRadius: '8px',
            borderColor: '#E5E7EB',
            color: '#4B5563',
            '&:hover': {
              borderColor: '#D1D5DB',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Exit
        </Button>
      </header>
      {/* Centered content */}
      <div
        style={{
          width: '100%',
          margin: '0 auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0, // Important for flex child to allow scrolling
        }}
      >
        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: 0, // Important for flex child to allow scrolling
            overflow: 'hidden', // Prevent main from growing beyond viewport
            maxWidth: wideLayout ? '1200px' : '720px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {/* Scrollable content container */}
          <div
            style={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden', // Prevent horizontal scroll
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
              scrollBehavior: 'smooth', // Smooth scrolling behavior
            }}
          >
            {/* Title and Description Section */}
            {(title || description) && (
              <Box
                sx={{
                  textAlign: 'center',
                  mb: 4,
                  maxWidth: 800,
                  width: '100%',
                  flexShrink: 0, // Prevent title section from shrinking
                }}
              >
                {title && (
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                      mb: description ? 1 : 0,
                      fontSize: { xs: '1.75rem', md: '2.125rem' },
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {description && (
                  <Typography
                    variant='body1'
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
            )}

            {/* Step Content */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0, // Prevent content from shrinking
                pb: 2, // Add bottom padding for better spacing
              }}
            >
              {children}
            </Box>
          </div>
        </main>
      </div>
      {/* Sticky Footer always at the bottom */}
      <StickyFooterTemplate>
        <style>{`.RSPStickyFooterTemplate > .RSPDivider { display: none !important; }`}</style>
        <Box
          sx={{
            height: '72px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            px: '1rem',
          }}
        >
          {/* Progress bar container - negative margin to extend full width */}
          <Box sx={{ width: '100%', mx: '-1rem' }}>
            {showDividerInsteadOfProgress ? (
              <Divider />
            ) : (
              <LinearProgressStep
                currentStep={currentStep}
                total={total}
                currentSubStepPercentage={currentSubStepPercentage}
                width='calc(100% + 2rem)'
              />
            )}
          </Box>
          {/* Button container - inherits padding for alignment */}
          <Box
            sx={{
              marginTop: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              onClick={onBack || (() => {})}
              disabled={backDisabled || false}
              variant='outlined'
              color='secondary'
              startIcon={<ArrowBackIcon />}
              sx={{
                ml: { xs: 0, sm: 0 },
                minWidth: '100px',
                fontSize: '14px',
                fontWeight: 500,
                padding: '10px 20px',
                borderRadius: '4px',
                textTransform: 'none',
                borderColor: '#DDDDDD',
                color: '#333333',
                '&:hover': {
                  borderColor: '#BBBBBB',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-disabled': {
                  borderColor: '#EEEEEE',
                  color: '#CCCCCC',
                },
              }}
            >
              {backLabel}
            </Button>
            <Button
              onClick={onNext || (() => {})}
              disabled={nextDisabled || false}
              variant={nextButtonVariant}
              color={nextButtonColor}
              endIcon={<ArrowForwardIcon />}
              sx={{
                mr: { xs: 0, sm: 0 },
                minWidth: '100px',
                fontSize: '14px',
                fontWeight: 500,
                padding: '10px 20px',
                borderRadius: '4px',
                backgroundColor: '#000000',
                color: '#FFFFFF',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#333333',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#DDDDDD',
                  color: '#999999',
                },
              }}
            >
              {nextLabel}
            </Button>
          </Box>
        </Box>
      </StickyFooterTemplate>
    </div>
  );
};
